<script setup lang="ts">
import { computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { SelectTeamDataInjection, type ChampionshipRegistrationContactPerson } from "../championship-registration"

const props = defineProps<{
    modelValue: ChampionshipRegistrationContactPerson | undefined
    label?: string
    /** Only persons named in a team are selectable, everyone else is listed disabled */
    requireRegistration?: boolean
}>()
const emit = defineEmits<{
    (e: "update:modelValue", value: ChampionshipRegistrationContactPerson | undefined): void
}>()

const { t } = useI18n()
const data = inject(SelectTeamDataInjection)

// Bound by id: the saved contact person and the options are different objects with different fields
const selectedId = computed({
    get: () => props.modelValue?.id ?? "",
    set: id => {
        emit(
            "update:modelValue",
            data?.value?.availableContactPersons.find(p => p.id === id),
        )
    },
})

/** The details shown in parentheses after the name: email (or that it's missing) and, if relevant, missing registration */
function optionDetails(member: ChampionshipRegistrationContactPerson) {
    const details = [member.email || t("@tasks:tasks.championship_registration.contact_person_missing_mail")]
    if (props.requireRegistration && member.registered === false) {
        details.push(t("@tasks:tasks.championship_registration.contact_person_not_registered"))
    }

    return details.join(", ")
}
</script>
<template>
    <div class="my-3" v-if="(data?.availableContactPersons?.length ?? 0) > 0">
        <b>{{ label ?? $t("@championship:registration.window.edit_team.contactPerson.select_member") }}</b>
        <select class="form-control" v-model="selectedId">
            <option value=""></option>
            <option
                v-for="member of data?.availableContactPersons"
                :value="member.id"
                :key="member.id"
                :disabled="!member.email || (requireRegistration && member.registered === false)"
            >
                {{ member.name }} ({{ optionDetails(member) }})
            </option>
        </select>
    </div>
    <div class="mb-5 mt-4" v-else>{{ $t("@tasks:tasks.championship_registration.no_valid_managers") }}</div>
</template>
